import {
  AnchorButton,
  Button,
  Divider,
  Icon,
  Menu,
  MenuItem,
  Navbar,
  NavbarDivider,
  NavbarGroup,
  NavbarHeading,
  Popover,
  PopoverInteractionKind,
  Position
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import config from '../config'
import Channels, { channelById } from '../constants/Channels'
import { usePermissions } from '../hooks/usePermissions'
import { regexMatchers } from '../hooks/usePermissionsMatchers'
import PrintNavbarSettings from '../printing/components/PrintNavbarSettings'
import { AppState } from '../store'
import { UprightStyle } from '../types'
import EmployeeSwitcher from './EmployeeSwitcher'
import Logo from './Logo'

// Hack for typescript to allow us to retrieve these properties from window
declare global {
  interface Window {
    orgConfig: any
    current: any
    user: any
  }
}

const HOME_PATH = '/'

const styles: UprightStyle = {
  navBar: { width: 'auto', height: 'auto' },
  clearFix: { clear: 'both' }
}

export const Help = () => (
  <Popover
    target={<Button minimal icon={IconNames.HELP} />}
    interactionKind={PopoverInteractionKind.HOVER}
    content={
      <Menu>
        <MenuItem target="_blank" href="https://help.uprightlabs.com/en-us/lister" text="Help center" />
        <MenuItem target="_blank" href="https://uprightlabs.canny.io/" text="Product roadmap" />
        <MenuItem
          target="_blank"
          href="https://qstf6.share.hsforms.com/2tYvpXCp9RoCrlcJ0mC1Qvw"
          text="Request a feature"
        />
        <MenuItem target="_blank" href="https://status.uprightlabs.com" text="Status page" />
        <MenuItem target="_blank" href="https://www.uprightlabs.com/privacy/" text="Privacy policy" />
      </Menu>
    }
    portalClassName="navigation-portal-pull-front"
  />
)

export const Home = () => (
  <Link to={HOME_PATH}>
    <Button className="bp3-minimal" icon="home">
      Home
    </Button>
  </Link>
)

export const NewProduct = () => {
  const { permissions } = usePermissions()
  if (!permissions.includes('products.post')) return null

  return (
    <Popover
      position={Position.BOTTOM_LEFT}
      portalClassName="navigation-portal-pull-front"
      hoverOpenDelay={80}
      content={
        <Menu>
          <Link to="/products/new">
            <MenuItem text="New product" icon={IconNames.NEW_LINK} tagName="div" />
          </Link>
          <a href="/products/new?draft=true">
            <MenuItem text="Draft product" icon={IconNames.ANNOTATION} tagName="div" />
          </a>
          {config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning') && (
            <Link to="/scan-to-list-books">
              <MenuItem text="Scan to list books" icon={IconNames.BARCODE} tagName="div" />
            </Link>
          )}
        </Menu>
      }
      interactionKind={PopoverInteractionKind.HOVER}
    >
      <AnchorButton minimal icon="add" href="/products/new">
        New Product
      </AnchorButton>
    </Popover>
  )
}

// There are two shipment page navigation buttons in the top navigation bar. This one sits beside the home button.
export const Shipments = () => {
  const { permissions } = usePermissions()
  const userRole = useSelector((s: AppState) => s.current.user.role)

  if (!permissions.includes('shipping.all')) {
    return null
  }

  // 2021-10-19 Admin check intentionally left to keep UX similar to legacy permissions system.
  // Consider removing this in the future.
  if (userRole !== 'admin') {
    return (
      <Link to="/shipments" title="Shipments">
        <Button className="bp3-minimal" icon="box">
          Shipments
        </Button>
      </Link>
    )
  }
  return null
}

export const AdminShipments = () => {
  const userRole = useSelector((s: AppState) => s.current.user.role)
  if (userRole !== 'admin') {
    return null
  }

  return (
    <Link to="/shipments" title="Shipments">
      <Button style={{ marginBottom: -4 }} className="bp3-minimal" icon={IconNames.BOX} />
    </Link>
  )
}

export const getMarketLink = (market: any) => {
  const channel = channelById(market.id)
  if (!channel || channel.name === 'bluebox' || channel.name === 'offer_up') return null

  if (market.id === Channels.SHOPIFY.id) {
    return `/listings/shopify_account/${market.market_account_id}`
  }
  if (market.id === Channels.EBAY.id) {
    return `/listings/ebay_account/${market.market_account_id}`
  }
  if (market.id === Channels.DEPOP.id) {
    return `/listings/depop_account/${market.market_account_id}`
  }
  return `/listings/${channel.name}`
}

function getNavigableListingMarkets(): any[] {
  const markets = window.orgConfig.markets ?? []
  // facebook is excluded explicitly because getMarketLink does not filter it;
  // bluebox and offer_up are already excluded by getMarketLink returning null.
  return markets.filter((m: any) => channelById(m.id)?.name !== 'facebook' && getMarketLink(m) != null)
}

export const ListingManager = () => {
  const { permissions } = usePermissions()
  if (!permissions.includes('listings.view')) return null

  const navigableMarkets = getNavigableListingMarkets()
  if (navigableMarkets.length === 0) return null

  if (navigableMarkets.length < 2) {
    const to = getMarketLink(navigableMarkets[0])!
    return (
      <Link to={to} title="Listings">
        <Button icon={IconNames.PROPERTY} minimal>
          Listings
        </Button>
      </Link>
    )
  }

  return (
    <Popover
      interactionKind={PopoverInteractionKind.HOVER}
      position={Position.BOTTOM_RIGHT}
      portalClassName="navigation-portal-pull-front"
      content={
        <ul className="bp3-menu">
          {navigableMarkets.map(m => (
            <li key={m.id}>
              <Link className="bp3-menu-item" to={getMarketLink(m)!}>
                {m.full_human_readable_name}
              </Link>
            </li>
          ))}
        </ul>
      }
      target={
        <Button icon={IconNames.PROPERTY} minimal>
          Listings
        </Button>
      }
    />
  )
}

export const Products = () => {
  const { permissions } = usePermissions()

  if (!permissions.includes('products.view')) return null

  // The reason we're using 'a' tag is to fall back to browser navigation, instead of React Router DOM.
  // This will ensure that the Redux state is clean when we navigate.
  return (
    <a href="/products">
      <Popover
        position={Position.BOTTOM_LEFT}
        portalClassName="navigation-portal-pull-front"
        content={
          <Menu>
            <a href="/products?draft=true&recycled=false&active=false">
              <MenuItem text="Drafts" tagName="div" />
            </a>
            {config.EXPRESS_LIST() && (
              <Link to="/express_list">
                <MenuItem text="Express List" tagName="div" />
              </Link>
            )}
            {config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning') && (
              <Link to="/scan-books">
                <MenuItem text="Scan Books" tagName="div" />
              </Link>
            )}
          </Menu>
        }
        interactionKind={PopoverInteractionKind.HOVER}
      >
        <Button minimal icon={IconNames.CUBE}>
          Products
        </Button>
      </Popover>
    </a>
  )
}

export const Manifests = () => {
  const { matchesSomePermissions } = usePermissions()

  if (!matchesSomePermissions(regexMatchers.anyManifestPermission)) return null

  return (
    <Link to="/manifests" title="Manifests" data-testid="nav-btn-manifests">
      <Button className="bp3-minimal" icon="clipboard" />
    </Link>
  )
}

export const Orders = () => {
  const { permissions } = usePermissions()

  if (!permissions.includes('orders.all')) {
    return null
  }
  return (
    <Link to="/orders" title="Orders">
      <Button style={{ marginBottom: -4 }} className="bp3-minimal" icon="shopping-cart" />
    </Link>
  )
}

export const Reports = () => {
  const { includesAllPermissions, matchesSomePermissions } = usePermissions()

  if (!matchesSomePermissions('reports.*')) return null

  const allButSupplierReports = /reports\.[^supplier]/
  const canOnlyAccessSupplierReports =
    includesAllPermissions('reports.supplier') && !matchesSomePermissions(allButSupplierReports)
  if (canOnlyAccessSupplierReports) {
    return (
      <Link to="/reports/stores" title="Reports" data-testid="reports-nav-button">
        <Button className="bp3-minimal" icon="chart" />
      </Link>
    )
  }

  return (
    <Link to="/reports" title="Reports" data-testid="reports-nav-button">
      <Button className="bp3-minimal" icon="chart" />
    </Link>
  )
}

export const AdminSettings = () => {
  const { matchesSomePermissions } = usePermissions()
  if (!matchesSomePermissions('settings.*')) return null

  return (
    <Link to="/admin" title="Admin Settings" data-testid="nav-btn-admin-settings">
      <Button className="bp3-minimal" icon="cog" />
    </Link>
  )
}

export const UserSettings = () => (
  <Popover
    interactionKind={PopoverInteractionKind.HOVER}
    position={Position.BOTTOM_RIGHT}
    content={
      <ul className="bp3-menu">
        <li style={{ padding: 5 }}>
          Signed in as <strong>{config.user.username}</strong>
        </li>
        <Divider />
        <li>
          <Link to="/edit_account" className="bp3-menu-item">
            <Icon icon={IconNames.COG} style={{ marginRight: 10 }} />
            Edit Account
          </Link>
        </li>
        <li>
          <a
            href="/signout"
            className="bp3-menu-item"
            onClick={() => {
              const hsc = (window as any).HubSpotConversations
              if (hsc?.clear) {
                hsc.clear({ resetWidget: true })
              }
            }}
          >
            <Icon icon={IconNames.LOG_OUT} style={{ marginRight: 10 }} />
            Sign Out
          </a>
        </li>
      </ul>
    }
    target={<Button className="bp3-minimal" icon={IconNames.USER} />}
    portalClassName="navigation-portal-pull-front"
  />
)

// Intentional role string check - Canny is magically bound the admin role
// user.role is transformed from the role relation to match the legacy RBAC format (snakecase)
export const Notification = () => {
  const userRole = useSelector((s: AppState) => s.current.user.role)

  return (
    userRole === 'admin' && (
      <Button
        className="nav-notifications"
        data-testid="nav-btn-notifications"
        data-canny-changelog
        icon={IconNames.NOTIFICATIONS}
        minimal
      />
    )
  )
}

export default () => (
  <Navbar style={styles.navBar}>
    <NavbarGroup>
      <Link to={HOME_PATH}>
        <Logo />
      </Link>
      {config.user.employee ? (
        <EmployeeSwitcher />
      ) : (
        <NavbarHeading>
          <Link to={HOME_PATH}>
            <Button className="bp3-minimal">{config.user.organization.name}</Button>
          </Link>
        </NavbarHeading>
      )}
    </NavbarGroup>
    <NavbarGroup align="right">
      <Home />
      <Shipments />
      <NewProduct />
      <Products />
      <ListingManager />
      <NavbarDivider />
      <Manifests />
      <Orders />
      <AdminShipments />
      <Reports />
      <AdminSettings />
      <UserSettings />
      <PrintNavbarSettings />
      <Notification />
      <Help />
    </NavbarGroup>
    <div style={styles.clearFix} />
  </Navbar>
)
